import { memo, useReducer } from 'react';

import { SquarePen, Trash2 } from 'lucide-react';
import { omit } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { CasinoAbTestFormDialog } from '../casinoAbTestFormDialog/CasinoAbTestFormDialog';
import { CasinoAbTestForm } from '../casinoAbTestFormDialog/form';

import { CasinoAbTestStatistic } from './casinoAbTestStatistic/CasinoAbTestStatistic';

import css from './CasinoAbTestCard.module.css';

import { abTestsService } from 'v2/api/abTestsService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { CasinoAbTestModel } from 'v2/types/AbTestsModels';
import { handleHttpError } from 'v2/utils';

interface CasinoAbTestCardProps {
  test: CasinoAbTestModel;
  onRevalidate: VoidFunction;
}

export const CasinoAbTestCard = memo<CasinoAbTestCardProps>(({ test, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    ['updateCasinoAbTest', test.id],
    abTestsService.updateCasinoAbTest,
    {
      onSuccess: () => {
        toast.success('The test has been updated successfully');
        onRevalidate();
        setOpen();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDelete } = useSWRMutation(
    ['deleteCasinoAbTest', test.id],
    abTestsService.deleteCasinoAbTest,
    {
      onSuccess: () => {
        onRevalidate();
      },
      onError: handleHttpError,
    }
  );

  const handleSubmit = (values: CasinoAbTestForm, resetForm: VoidFunction) => {
    triggerUpdate(values).then(() => resetForm());
  };

  return (
    <div className={css.casinoAbTestCard}>
      <div className={css.casinoAbTestCardHeader}>
        <p className="truncate">{test.name}</p>

        <Chip
          label={test.isActive ? 'Active' : 'Inactive'}
          color={test.isActive ? 'success' : 'default'}
          size="small"
        />
      </div>

      <div className={css.casinoAbTestCardActions}>
        <CasinoAbTestStatistic test={test} />

        <Tooltip title="Edit" placement="top">
          <IconButton onClick={setOpen} color="warning">
            <SquarePen width={18} height={18} />
          </IconButton>
        </Tooltip>

        <Popconfirm message="Are you sure that you want to delete this test?" onConfirm={triggerDelete}>
          <Tooltip title="Delete" placement="top">
            <IconButton color="error">
              <Trash2 width={18} height={18} />
            </IconButton>
          </Tooltip>
        </Popconfirm>
      </div>

      <CasinoAbTestFormDialog
        title="Edit AB Test"
        initialValues={omit(test, ['id'])}
        isOpen={isOpen}
        onClose={setOpen}
        isSubmitting={isMutating}
        onSubmit={handleSubmit}
        isEdit
      />
    </div>
  );
});

CasinoAbTestCard.displayName = 'CasinoAbTestCard';
